import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { addPermissions, WithPermissions } from 'decorators/addPermissions';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import { StoreProps } from 'store';
import PaymentItemType from 'types/savedEntity/SavedPayment';
import PaymentPageUI from 'types/PaymentPageUI';
import Mapper from 'helpers/Mapper';
import Operations from 'helpers/Operations';
import Messages from 'constants/rpcTypes';
import permissionReasons from 'constants/permissionReasons';

import { Config } from 'components/paymentCard/PaymentCardContainer';
import { usePaymentCard } from 'components/paymentCard/usePaymentCard';

import OperationInfoWl19 from 'components/paymentCard/operationInfo/OperationInfo.wl19';
import CopyToClipboard from 'components/ui/copyToClipboard';
import Icon from 'components/ui/icon';
import RoundButton from 'components/ui/roundButton';
import { Skeleton } from 'components/ui/skeleton';
import Teaser from 'components/ui/teaser';
import InlineModal from 'components/modal/InlineModal';
import ModalTitle from 'components/modal/components/ModalTitle';
import ModalContent from 'components/modal/components/ModalContent';
import PaymentActions from 'components/paymentCard/paymentActions/PaymentActionsContainer';
import CustomScrollbar from 'components/ui/customScrollbar';
import OperationItem from './operationItem';
import PaymentInfo from 'components/paymentCard/paymentInfo';
import Animation from 'components/ui/animation';
import NoContent from 'components/ui/noContent';
import PageTemplate from 'components/pageTemplate/PageTemplate';
import Button from 'components/ui/button';
import LabelStatus from 'components/ui/labelStatus';
import Panel from 'components/ui/panel';
import AddBlackList from 'components/paymentCard/panelBlackList/AddBlackListContainer';
import ButtonReference from 'components/paymentCard/buttonReference';
import CreateSupportTicketButton from 'components/createSupportTicketButton/CreateSupportTicketButton';
import { ReactComponent as NoContentImage } from 'images/no-content.svg';
import { WL_ID_19 } from 'constants/wlIds';

interface Props extends IntlProps, WithPermissions, StoreProps {
    payment?: PaymentItemType;
    config?: Config;
    projectId?: string;
    isLoading: boolean;
    syncPaymentUI: (id: string, ui: PaymentPageUI) => void;
    fetchData: () => void;
}

function PaymentCardWl19(props: Props) {
    const { payment, isLoading, getTranslate, config } = props;
    const { isManagePayment, isCustomerInfo, isRemittanceRecipientAccount } =
        config || {};

    const [isPaymentInfoModalOpen, setIsPaymentInfoModalOpen] =
        React.useState(false);

    const {
        pageUI,
        isActionsOpened,
        changePageUI,
        setIsActionsOpened,
        changeSort,
        openRetries,
        closeRetries,
        openRetryCancelModal,
        getSortedOperations,
        getCurrentOperations,
        getCurrentSelectedOperation,
        getStatusTooltip,
        isEnabled,
        isDisabledByReason,
    } = usePaymentCard(props);

    const { operations } = payment?.data || {};
    const { transactionId, projectId, paymentId, paymentStatus } =
        payment?.data?.payment || {};

    // ─── Operations list ─────────────────────────────────────────────────────────

    const renderOperationsList = () => {
        const {
            isRetriesOpen,
            parentSelectedOperationId,
            operationOrder,
            selectedOperationId,
            operationsScrollTop,
        } = pageUI;

        const operationsCount = !isLoading && operations ? operations.length : '';
        const operationsDuration =
            !isLoading && operations ? Operations.getDuration(operations || []) : '';
        const operationsCountText = getTranslate('payments.single.operation', {
            operationsCount,
        });
        const operationDurationText = getTranslate('payments.single.duration', {
            operationsDuration,
        });
        const paymentOperationCount = `${operationsCountText} | ${operationDurationText}`;
        const paymentTitle = getTranslate('payments.data.paymentOperations.header');
        const retryTitle = getTranslate('subscriptions.single.recurringRetries.header');

        const sortedOperations =
            isLoading || !operations
                ? []
                : getSortedOperations(getCurrentOperations(operations), operationOrder);

        return (
            <div className='payment-card__operations-card card flex h-full flex-col'>
                <div className='payment-card__operations-header relative flex items-center justify-between gap-2 px-5 py-4'>
                    <div className='payment-card__operations-header-info overflow-hidden'>
                        <div className='payment-card__operations-title text-l-bold mb-1'>
                            {isRetriesOpen ? retryTitle : paymentTitle}
                        </div>
                        {operationsDuration ? (
                            <div className='payment-card__operations-meta text-m-regular text-text-secondary flex gap-1'>
                                <span>{getTranslate('subscriptions.single.Id.label')}</span>
                                <span className='payment-card__operations-meta-id utils-overflow-dots'>
                                    {isRetriesOpen ? parentSelectedOperationId : paymentOperationCount}
                                </span>
                                {isRetriesOpen ? (
                                    <CopyToClipboard text={parentSelectedOperationId} />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                    <div className='payment-card__operations-controls flex items-center gap-2'>
                        {isRetriesOpen ? (
                            <RoundButton
                                size={26}
                                icon='im-Arrow-left-Option-2'
                                iconSize={12}
                                onClick={closeRetries}
                                customClass='payment-card__operations-back'
                            />
                        ) : null}
                        {operations?.length ? (
                            <RoundButton
                                size={26}
                                icon='im-Sort'
                                iconSize={12}
                                onClick={changeSort}
                                customClass={classNames('payment-card__operations-sort', {
                                    'rotate-180': operationOrder === 'asc',
                                })}
                            />
                        ) : null}
                    </div>
                </div>

                <div className='payment-card__operations-content relative h-full min-h-0'>
                    {isLoading || !operations ? (
                        <div className='payment-card__operations-skeleton'>
                            <Skeleton width='100%' height={96} />
                        </div>
                    ) : (
                        <CustomScrollbar
                            initialScrollTop={operationsScrollTop}
                            needResetScrollTop
                            onScrollStop={({ operationsScrollTop }) =>
                                changePageUI({ operationsScrollTop })
                            }>
                            <div className='payment-card__operations-list border-border-base h-full min-h-0 border-t pb-5'>
                                {sortedOperations.length
                                    ? sortedOperations.map((operation) => {
                                        const { operationId, operationStatus } = operation;
                                        return (
                                            <OperationItem
                                                key={operationId}
                                                customClass={classNames({
                                                    [`eto-operation-card__${operationStatus}`]:
                                                        Boolean(operationStatus),
                                                })}
                                                data={operation}
                                                isCurrent={operationId === selectedOperationId}
                                                onSelect={(id) =>
                                                    changePageUI({ selectedOperationId: id })
                                                }
                                                onStatusClick={(id) => openRetries(id)}
                                                wlId={WL_ID_19}
                                            />
                                        );
                                    })
                                    : null}
                            </div>
                        </CustomScrollbar>
                    )}
                </div>
            </div>
        );
    };

    // ─── Operation info ───────────────────────────────────────────────────────────

    const renderOperationInfoTabs = (ops: any[]) => {
        const selectedOperation = getCurrentSelectedOperation(ops);
        if (!selectedOperation) return null;

        return (
            <OperationInfoWl19
                transactionId={transactionId}
                operation={selectedOperation}
            />
        );
    };

    const renderOperationInfoActions = (ops: any[]) => {
        const { isRetriesOpen } = pageUI;
        const { isAddToList } = config || {};
        const {
            operationId,
            operationType,
            availableActions,
            mapped_recurring_retry_status: retryStatus,
        } = getCurrentSelectedOperation(ops);
        const referenceButtonEnabled =
            availableActions?.operationReferenceOrder?.isEnabled;
        const isEnabledToCreateTicket =
            isEnabled(Messages.PaymentIssue_PerformAction) ||
            isDisabledByReason(
                Messages.PaymentIssue_PerformAction,
                permissionReasons.REASON_IS_NOT_AVAILABLE_FOR_SUPPORT
            );
        const isCancelEnabled =
            isEnabled(Messages.SubscriptionPayment_RetryCancel) ||
            isDisabledByReason(
                Messages.SubscriptionPayment_RetryCancel,
                permissionReasons.REASON_IS_NOT_AVAILABLE_FOR_SUPPORT
            );

        const actions = (
            <>
                {referenceButtonEnabled ? (
                    <ButtonReference
                        transactionId={transactionId}
                        operationId={operationId}
                    />
                ) : null}
                {isEnabledToCreateTicket ? (
                    <CreateSupportTicketButton
                        operationId={operationId}
                        transactionId={transactionId}
                    />
                ) : null}
                {operationType === 'recurring' ? (
                    <PaymentActions
                        key={transactionId}
                        isNeedPing={false}
                        operationId={operationId}
                        transactionId={transactionId}
                        projectId={projectId}
                        refundTooltip={getTranslate(
                            'payments.data.operationRefund.info.tooltip'
                        )}
                        customClass='payment-actions_card'
                        buttonTheme='primary'
                    />
                ) : null}
            </>
        );

        return (
            <div className='payment-card__actions mt-4 flex flex-wrap gap-2'>
                {retryStatus ? (
                    <>
                        {isRetriesOpen ? (
                            <Button
                                status='outline'
                                text={getTranslate('payments.single.backToMainOperation.button')}
                                onClick={closeRetries}
                            />
                        ) : (
                            <>
                                <Button
                                    status='primary'
                                    text={getTranslate('payments.single.showretries.button')}
                                    onClick={() => openRetries(operationId)}
                                />
                                <Button
                                    status='danger-outline'
                                    text={getTranslate('payments.single.cancelretries.button')}
                                    onClick={openRetryCancelModal}
                                    disabled={
                                        !isCancelEnabled || retryStatus !== 'retries active'
                                    }
                                />
                            </>
                        )}
                        <div className='relative'>
                            <Button
                                icon='im-Dots'
                                status='primary'
                                customClass='payment-card__actions-toggle h-[32px] w-[32px] rounded-full'
                                onClick={() => setIsActionsOpened(true)}
                            />
                            <Panel
                                isOpened={isActionsOpened}
                                renderCloseControl={false}
                                contentStyle={{ padding: 12, overflow: 'unset' }}
                                onClose={() => setIsActionsOpened(false)}
                                customClass='max-tablet:left-[unset] max-tablet:right-[-40px] top-[40px] right-[unset] left-0'>
                                <div className='payment-card__actions-panel flex flex-col gap-1'>
                                    {actions}
                                </div>
                            </Panel>
                        </div>
                    </>
                ) : (
                    actions
                )}
            </div>
        );
    };

    const renderOperationInfoHeader = (ops: any[]) => {
        const selectedOperation = getCurrentSelectedOperation(ops);
        if (!isLoading && !selectedOperation) return null;

        const {
            operationId,
            next_retry_date: retryDate,
            mapped_recurring_retry_status: retryStatus,
        } = selectedOperation;

        return (
            <div className='payment-card__operation-info-header p-5'>
                <div className='payment-card__operation-info-header-row flex flex-wrap items-center justify-between gap-2'>
                    <div className='payment-card__operation-info-id-block'>
                        <div className='payment-card__operation-info-id-label text-m-regular text-text-secondary mb-1'>
                            {getTranslate('payments.single.operationCard.header')} ID
                        </div>
                        <div className='payment-card__operation-info-id-value font-heading text-heading-m flex items-center gap-2'>
                            <span className='payment-card__operation-info-id-text'>
                                {operationId}
                            </span>
                            <CopyToClipboard
                                customClass='payment-card__operation-info-id-copy'
                                text={operationId}
                            />
                        </div>
                    </div>
                    {retryStatus ? (
                        <div className='payment-card__operation-info-status'>
                            <LabelStatus
                                text={retryStatus}
                                status={Mapper.getStatus(retryStatus)}
                                customClass='rounded-[44px] px-[10px] py-[3px] leading-[20px] capitalize'
                            />
                        </div>
                    ) : null}
                </div>
                {retryStatus ? (
                    <div className='payment-card__operation-info-tooltip pt-3'>
                        {getStatusTooltip(retryStatus, retryDate)}
                    </div>
                ) : null}
                <div className='payment-card__operation-info-actions'>
                    {renderOperationInfoActions(ops)}
                </div>
            </div>
        );
    };

    const renderOperationInfo = () => {
        const { parentSelectedOperationId } = pageUI;
        const noContentText = getTranslate(
            'subscriptions.single.recurringRetries.noInfo'
        );

        let currentOperation: any[] | null = null;
        if (operations && !isLoading) {
            currentOperation = getCurrentOperations(operations);
        }

        if (currentOperation && !currentOperation.length) {
            const selectedOperation = operations?.find(
                ({ operationId }) => operationId === parentSelectedOperationId
            );
            const { next_retry_date: retryDate } = selectedOperation;

            return (
                <div className='payment-card__no-content card flex h-full items-center justify-center'>
                    <div className='payment-card__no-content-inner px-5'>
                        <div className='payment-card__no-content-body flex flex-col items-center justify-center gap-3 text-center'>
                            <NoContentImage />
                            <h1 className='payment-card__no-content-title font-heading text-heading-m'>
                                {noContentText}
                            </h1>
                            {retryDate ? (
                                <p className='payment-card__no-content-text text-m-regular max-w-[305px]'>
                                    {getTranslate(
                                        'subscriptions.single.recurringRetries.nextTry'
                                    )}
                                    &nbsp;
                                    <span className='text-m-semi-bold'>{retryDate}</span>
                                </p>
                            ) : null}
                            <Button
                                status='outline'
                                text={getTranslate(
                                    'payments.single.backToMainOperation.button'
                                )}
                                onClick={closeRetries}
                                customClass='payment-card__no-content-button'
                            />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className='payment-card__operation-info-body flex h-full flex-col gap-4'>
                <div className='payment-card__operation-info-card card'>
                    {isLoading || !operations ? (
                        <div className='payment-card__operation-info-skeleton p-5'>
                            <Skeleton width='100%' height={80} />
                        </div>
                    ) : (
                        renderOperationInfoHeader(currentOperation ?? [])
                    )}
                </div>
                <div className='payment-card__operation-info-tabs card relative h-full min-h-0'>
                    {isLoading || !operations ? (
                        <div className='payment-card__operation-info-skeleton p-5'>
                            <Skeleton width='100%' height={80} />
                        </div>
                    ) : (
                        renderOperationInfoTabs(currentOperation ?? [])
                    )}
                </div>
            </div>
        );
    };

    // ─── Teaser (wl19-specific: info button instead of sidebar toggle) ────────────

    const renderTeaser = () => (
        <Teaser
            title={getTranslate('payments.data.payment.header')}
            text={
                paymentId ? (
                    <div className='payment-card__teaser-id flex items-end gap-2'>
                        <span className='payment-card__teaser-id-text'>{paymentId}</span>
                        <CopyToClipboard
                            text={paymentId}
                            customClass='payment-card__teaser-id-copy text-text-always-white hover:opacity-80'
                        />
                    </div>
                ) : (
                    ''
                )
            }
            status={paymentStatus}
            isLoading={isLoading}>
            <button
                type='button'
                className='payment-card__teaser-info-btn'
                onClick={() => setIsPaymentInfoModalOpen(true)}>
                <Icon name='etoInfoCircle' />
            </button>
            {isManagePayment ? (
                <PaymentActions
                    key={transactionId}
                    transactionId={transactionId}
                    projectId={projectId}
                    buttonTheme='light'
                    customClass='payment-card__teaser-actions payment-actions_card'
                />
            ) : null}
        </Teaser>
    );

    // ─── Payment info rendered inside InlineModal (wl19-specific) ────────────────

    const renderPaymentInfo = () => (
        <PaymentInfo
            theme='brand'
            isLoading={isLoading}
            closePaymentInfo={() => setIsPaymentInfoModalOpen(false)}
            data={payment?.data}
            pageUI={pageUI}
            onChangePageUI={changePageUI}
            isManagePayment={isManagePayment}
            isCustomerInfo={isCustomerInfo}
            isRemittanceRecipientAccount={isRemittanceRecipientAccount}
        />
    );

    // ─── Root render ─────────────────────────────────────────────────────────────

    return (
        <PageTemplate.Container customClass='overflow-y-auto'>
            <div className='max-tablet:h-auto size-full'>
                <Animation>
                    <div className='max-mobile-s:min-h-auto relative mx-auto size-full min-h-[610px] max-w-[1640px]'>
                        {payment?.data?.error ? (
                            <NoContent
                                text={getTranslate(
                                    'payments.single.paymentInfo.noPaymentDetails.text'
                                )}
                            />
                        ) : (
                            <div className='payment-card max-tablet:h-auto size-full'>
                                <div className='size-full'>
                                    <div className='payment-card__wrapper'>
                                        <div className='payment-card__payment-details'>
                                            {renderTeaser()}
                                        </div>
                                        <div className='payment-card__operations'>
                                            {renderOperationsList()}
                                        </div>
                                        <div className='payment-card__operation-info'>
                                            {renderOperationInfo()}
                                        </div>
                                        <InlineModal
                                            isOpened={isPaymentInfoModalOpen}
                                            modalId='payment-info'
                                            needCloseButton
                                            onClickAwayClose
                                            onClose={() =>
                                                setIsPaymentInfoModalOpen(false)
                                            }>
                                            <ModalTitle>
                                                {getTranslate(
                                                    'payments.single.paymentInfo'
                                                )}
                                            </ModalTitle>
                                            <ModalContent>
                                                {renderPaymentInfo()}
                                            </ModalContent>
                                        </InlineModal>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </Animation>
            </div>
        </PageTemplate.Container>
    );
}

export default connect()(addTranslation(addPermissions(PaymentCardWl19)));
